import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  useTheme,
  useColorMode,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Collapse,
  HStack,
  Code,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { cqAPI } from "../../lib/Auth";
import "../CallReport/CallReport.css";

function AuditTrail() {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState("Today");
  const [sortConfig, setSortConfig] = useState({ key: "created", direction: "descending" });
  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [startDate, endDate] = dateRange;

  const navigate = useNavigate();
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const formatDateTime = (value) =>
    value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "-";

  const updateUrlHash = (startDate, endDate, selectedRange) => {
    const queryParams = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
    }).toString();
    window.location.hash = queryParams;
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedLogs = useMemo(() => {
    const items = [...logs];
    if (sortConfig?.key) {
      items.sort((a, b) => {
        const getVal = (obj) =>
          sortConfig.key === "created" ? new Date(obj[sortConfig.key]) : obj[sortConfig.key];
        const va = getVal(a);
        const vb = getVal(b);
        if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [logs, sortConfig]);

  const fetchAudit = async (params = {}) => {
    setIsLoading(true);
    try {
      const formattedStartDate = params.startDate
        ? moment(params.startDate).format("L")
        : moment(dateRange[0]).format("L");
      const formattedEndDate = params.endDate
        ? moment(params.endDate).format("L")
        : moment(dateRange[1]).format("L");

      const qs = new URLSearchParams({
        rangeBeg: formattedStartDate,
        rangeEnd: formattedEndDate,
      });

      const response = await cqAPI.get(`/audit?${qs.toString()}`);
      setLogs(response?.data || []);
    } catch (err) {
      console.error("Error fetching audit trail:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let start = null;
    let end = null;
    switch (value) {
      case "Today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "Yesterday":
        start = moment().subtract(1, "days").startOf("day");
        end = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        start = moment().subtract(6, "days").startOf("day");
        end = moment().endOf("day");
        break;
      case "Last 4 weeks":
        start = moment().subtract(4, "weeks").startOf("day");
        end = moment().endOf("day");
        break;
      case "Month to Date":
        start = moment().startOf("month");
        end = moment().endOf("day");
        break;
      case "Quarter to date":
        start = moment().startOf("quarter");
        end = moment().endOf("day");
        break;
      case "Year to date":
        start = moment().startOf("year");
        end = moment().endOf("day");
        break;
      default:
        start = null;
        end = null;
    }
    setDateRange([start ? start.toDate() : null, end ? end.toDate() : null]);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Flex direction="column" width="100%" px={4}>
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500" mr={2}>
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" pt={2}>
        <Box as="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>{date.toLocaleString("default", { month: "long", year: "numeric" })}</Box>
        <Box as="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  const hoverBgColor = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const s = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const e = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().endOf("day").toDate();
    const r = hashParams.get("selectedRange") || "Today";

    setDateRange([s, e]);
    setSelectedRange(r);

    fetchAudit({ startDate: s, endDate: e });
  }, []);

  useEffect(() => {
    updateUrlHash(startDate, endDate, selectedRange);
  }, [startDate, endDate, selectedRange]);

  const toggleExpand = (rowKey) => {
    setExpandedRows((prev) => {
      const set = new Set(prev);
      if (set.has(rowKey)) set.delete(rowKey);
      else set.add(rowKey);
      return set;
    });
  };

  const prettyPreview = (val) => {
    try {
      const str = typeof val === "string" ? val : JSON.stringify(val, null, 2);
      return str.length > 160 ? str.slice(0, 160) + "…" : str;
    } catch {
      return String(val);
    }
  };

  const copyToClipboard = async (val) => {
    try {
      const text = typeof val === "string" ? val : JSON.stringify(val, null, 2);
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.warn("Clipboard copy failed", e);
    }
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh" width="100%">
        <Spinner color="blue.500" size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={4} maxHeight="100vh" overflow="hidden" width="100%">
      <Flex justifyContent="flex-start" mb={4} wrap="wrap" gap={2}>
        <Select
          placeholder="Select range"
          value={selectedRange}
          onChange={(e) => handleDateRangeSelection(e.target.value)}
          className="date-type"
          w="180px"
        >
          <option value="Today">Today</option>
          <option value="Yesterday">Yesterday</option>
          <option value="Last 7 days">Last 7 days</option>
          <option value="Last 4 weeks">Last 4 weeks</option>
          <option value="Month to Date">Month to Date</option>
          <option value="Quarter to date">Quarter to date</option>
          <option value="Year to date">Year to date</option>
          <option value="Custom">Custom</option>
        </Select>

        <DatePicker
          className={`custom-datepicker ${colorMode === "light" ? "light-theme" : "dark-theme"}`}
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            setDateRange(update);
            setSelectedRange("Custom");
          }}
          isClearable
          renderCustomHeader={renderCustomHeader}
        />

        <Flex ml="auto">
          <Button onClick={() => fetchAudit({ startDate, endDate })}>Show logs</Button>
        </Flex>
      </Flex>

      {/* Scrollable Table Container */}
      <Box overflowX="auto" overflowY="auto" maxHeight="80vh">
        <Table
          variant="simple"
          size="sm"
          minWidth="900px"
          sx={{
            tableLayout: "fixed",
            th: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", py: 2 },
            td: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", py: 2 },
          }}
        >
          <Thead>
            <Tr>
              <Th onClick={() => requestSort("created")} cursor="pointer" width="180px">
                Date
              </Th>
              <Th onClick={() => requestSort("endpoint")} cursor="pointer" width="180px">
                Endpoint
              </Th>
              <Th onClick={() => requestSort("method")} cursor="pointer" width="80px">
                Method
              </Th>
              <Th onClick={() => requestSort("type")} cursor="pointer" width="90px">
                Type
              </Th>
              <Th onClick={() => requestSort("msg")} cursor="pointer" width="200px">
                Message
              </Th>
              <Th onClick={() => requestSort("user")} cursor="pointer" width="130px">
                User
              </Th>
              <Th onClick={() => requestSort("admin")} cursor="pointer" width="130px">
                Admin
              </Th>
              <Th width="120px">Body</Th>
            </Tr>
          </Thead>
          <Tbody fontSize="sm">
            {sortedLogs.map((log, idx) => {
              const rowKey = `${log.created}-${log.endpoint}-${idx}`;
              const isOpen = expandedRows.has(rowKey);
              return (
                <React.Fragment key={rowKey}>
                  <Tr _hover={{ bg: hoverBgColor }}>
                    <Td title={log.created}>{formatDateTime(log.created)}</Td>
                    <Td>{log.endpoint}</Td>
                    <Td>{log.method}</Td>
                    <Td>
                      <Tag
                        size="sm"
                        colorScheme={
                          log.type === "ok"
                            ? "green"
                            : log.type === "error"
                            ? "red"
                            : "gray"
                        }
                      >
                        {log.type || "-"}
                      </Tag>
                    </Td>
                    <Td title={log.msg}>{log.msg}</Td>
                    <Td>{log.user || "-"}</Td>
                    <Td>{log.admin || "-"}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => toggleExpand(rowKey)}
                          rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        >
                          {isOpen ? "Hide" : "View"}
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => copyToClipboard(log.body)}
                        >
                          Copy
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                  <Tr>
                    <Td colSpan={8} p={0}>
                      <Collapse in={isOpen} animateOpacity>
                        <Box
                          px={4}
                          py={3}
                          bg={colorMode === "light" ? "gray.50" : "gray.800"}
                          borderTopWidth="1px"
                        >
                          <Text fontWeight="medium" mb={2}>
                            Request Body
                          </Text>
                          <Box
                            as="pre"
                            fontSize="xs"
                            overflowX="auto"
                            p={3}
                            borderWidth="1px"
                            borderRadius="md"
                          >
                            {(() => {
                              try {
                                const text =
                                  typeof log.body === "string"
                                    ? log.body
                                    : JSON.stringify(log.body, null, 2);
                                return <Code whiteSpace="pre" p={0}>{text}</Code>;
                              } catch {
                                return (
                                  <Code whiteSpace="pre" p={0}>
                                    {prettyPreview(log.body)}
                                  </Code>
                                );
                              }
                            })()}
                          </Box>
                        </Box>
                      </Collapse>
                    </Td>
                  </Tr>
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default AuditTrail;
